import { createContext, useContext, useRef, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
    DotsThreeIcon,
    DotsThreeVerticalIcon,
    type Icon,
} from '@phosphor-icons/react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

export interface ActionMenuItem {
    key: string;
    label: ReactNode;
    icon?: Icon;
    variant?: 'default' | 'danger';
    disabled?: boolean;
    onSelect?: () => void;
    submenu?: ReactNode;
}

interface EntityActionsMenuContextValue {
    close: () => void;
}

const EntityActionsMenuContext =
    createContext<EntityActionsMenuContextValue | null>(null);

export function useEntityActionsMenu(): EntityActionsMenuContextValue | null {
    return useContext(EntityActionsMenuContext);
}

interface EntityActionsMenuProps {
    items: ActionMenuItem[];
    triggerTestId?: string;
    triggerAriaLabel?: string;
    wrapperClassName?: string;
    triggerButtonClassName?: string;
    triggerIconClassName?: string;
    triggerIconOrientation?: 'horizontal' | 'vertical';
    onOpenChange?: (open: boolean) => void;
}

export function EntityActionsMenu({
    items,
    triggerTestId,
    triggerAriaLabel,
    wrapperClassName = 'flex-shrink-0',
    triggerButtonClassName = 'p-1',
    triggerIconClassName = 'w-5 h-5 text-grey-200 hover:text-white-100 transition-colors',
    triggerIconOrientation = 'vertical',
    onOpenChange,
}: EntityActionsMenuProps) {
    const [open, setOpen] = useState(false);
    const contextValue = useMemo(() => ({ close: () => setOpen(false) }), []);
    const TriggerIcon =
        triggerIconOrientation === 'horizontal'
            ? DotsThreeIcon
            : DotsThreeVerticalIcon;

    if (items.length === 0) return null;

    return (
        <div
            className={cn(wrapperClassName)}
            onClick={(event) => event.stopPropagation()}
        >
            <DropdownMenu
                modal={false}
                open={open}
                onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    onOpenChange?.(nextOpen);
                }}
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="small"
                        shape="round"
                        onClick={(event) => event.stopPropagation()}
                        className={triggerButtonClassName}
                        data-testid={triggerTestId}
                        leadingIcon={
                            <TriggerIcon
                                className={triggerIconClassName}
                                weight="bold"
                            />
                        }
                        {...(triggerAriaLabel
                            ? { 'aria-label': triggerAriaLabel }
                            : {})}
                    />
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    side="bottom"
                    align="end"
                    sideOffset={5}
                    onClick={(event) => event.stopPropagation()}
                >
                    <EntityActionsMenuContext.Provider value={contextValue}>
                        <DropdownMenuGroup>
                            {items.map((item) => (
                                <ActionMenuRow key={item.key} item={item} />
                            ))}
                        </DropdownMenuGroup>
                    </EntityActionsMenuContext.Provider>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

const SUBMENU_CLOSE_DELAY_MS = 200;

function ActionMenuRow({ item }: { item: ActionMenuItem }) {
    const ItemIcon = item.icon;
    const variant = item.variant ?? 'default';
    const [subOpen, setSubOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>(null);

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(
            () => setSubOpen(false),
            SUBMENU_CLOSE_DELAY_MS,
        );
    };

    if (item.submenu) {
        return (
            <DropdownMenuSub open={subOpen} onOpenChange={setSubOpen}>
                <DropdownMenuSubTrigger
                    variant={variant}
                    disabled={item.disabled}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                >
                    {ItemIcon && (
                        <ItemIcon
                            size={16}
                            weight="regular"
                            className="flex-shrink-0"
                        />
                    )}
                    <span className="text-sm font-medium">{item.label}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                    className="min-w-0 border-0 bg-transparent p-0 shadow-none"
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                >
                    {item.submenu}
                </DropdownMenuSubContent>
            </DropdownMenuSub>
        );
    }

    return (
        <DropdownMenuItemBase
            variant={variant}
            disabled={item.disabled}
            {...(item.onSelect && !item.disabled
                ? { onSelect: () => item.onSelect?.() }
                : {})}
        >
            {ItemIcon && (
                <ItemIcon
                    size={16}
                    weight="regular"
                    className="flex-shrink-0"
                />
            )}
            <span className="text-sm font-medium">{item.label}</span>
        </DropdownMenuItemBase>
    );
}
